import React, { useEffect, useRef } from 'react';
import { useSettings } from '../context/SettingsContext';

function ParticlesCanvas() {
    const { settings } = useSettings();
    const enabled = settings.particlesEnabled;
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        let animationId;
        if (!enabled) {
            // Falls deaktiviert: canvas leeren
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            return; // Effekt hier abbrechen
        }
        let mouse = { x: null, y: null };

        let width = window.innerWidth;
        let height = window.innerHeight;
        canvas.width = width;
        canvas.height = height;

        let targetOffset = { x: 0, y: 0 };
        let currentOffset = { x: 0, y: 0 };

        const particles = [];
        const numberOfParticles = 60; // Anzahl der Partikel

        class Particle {
            constructor() {
                this.x = Math.random() * width;
                this.y = Math.random() * height;
                this.size = Math.random() * 3 + 2;
                this.speedX = (Math.random() - 0.5);
                this.speedY = (Math.random() - 0.5);
                this.color = this.getRandomColor();
                this.shape = Math.random() > 0.7 ? 'square' : 'circle';
                this.originalSize = this.size;
            }

            getRandomColor() {
                const colors = [
                    'rgba(0, 255, 255, 0.1)',  // cyan
                    'rgba(0, 191, 255, 0.1)',  // hellblau
                    'rgba(0, 255, 191, 0.1)'   // türkis
                ];
                return colors[Math.floor(Math.random() * colors.length)];
            }

            // Gibt die Koordinaten des Zentrums des Partikels zurück
            getCenter() {
                return {
                    x: this.x,
                    y: this.y
                };
            }

            update() {
                this.x += this.speedX;
                this.y += this.speedY;

                // Partikel wieder von oben erscheinen lassen
                if (this.y > height) this.y = 0;
                if (this.y < 0) this.y = height;
                if (this.x > width) this.x = 0;
                if (this.x < 0) this.x = width;

                // Größenanimation bei Mausnähe
                if (mouse.x !== null && mouse.y !== null) {
                    const dx = mouse.x - this.x;
                    const dy = mouse.y - this.y;
                    const dist = Math.hypot(dx, dy);
                    const magnetRadius = 200;
                    if (dist < magnetRadius) {
                        this.size = this.originalSize * (1 + (magnetRadius - dist) / magnetRadius);
                    } else {
                        this.size = this.originalSize;
                    }
                }
            }

            draw() {
                ctx.fillStyle = this.color;
                ctx.beginPath();
                if (this.shape === 'circle') {
                    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                } else {
                    ctx.rect(this.x - this.size / 2, this.y - this.size / 2, this.size, this.size);
                }
                ctx.shadowBlur = 5;
                ctx.shadowColor = 'cyan';
                ctx.closePath();
                ctx.fill();
            }
        }

        // Zeichne die Verbindungslinien zwischen den Partikeln
        const drawConnections = () => {
            for (let i = 0; i < particles.length; i++) {
                for (let j = i + 1; j < particles.length; j++) {
                    const p1 = particles[i];
                    const p2 = particles[j];

                    const center1 = p1.getCenter();
                    const center2 = p2.getCenter();

                    const dx = center1.x - center2.x;
                    const dy = center1.y - center2.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance < 100) {
                        ctx.beginPath();
                        ctx.strokeStyle = `rgba(0, 255, 255, ${0.1 - distance/1000})`;
                        ctx.moveTo(center1.x, center1.y);
                        ctx.lineTo(center2.x, center2.y);
                        ctx.stroke();
                    }
                }
            }
        };

        const throttle = (func, limit) => {
            let inThrottle;
            return function(...args) {
                if (!inThrottle) {
                    func.apply(this, args);
                    inThrottle = true;
                    setTimeout(() => inThrottle = false, limit);
                }
            }
        };

        const createParticles = () => {
            for (let i = 0; i < numberOfParticles; i++) {
                particles.push(new Particle());
            }
        };

        const animate = () => {
            ctx.clearRect(0, 0, width, height);
            currentOffset.x += (targetOffset.x - currentOffset.x) * 0.06;
            currentOffset.y += (targetOffset.y - currentOffset.y) * 0.06;

            ctx.save();
            ctx.translate(currentOffset.x, currentOffset.y); // leichte Verschiebung anwenden

            // Zuerst alle Verbindungen zeichnen
            drawConnections();

            // Dann alle Partikel aktualisieren und zeichnen
            particles.forEach((particle) => {
                particle.update();
                if (mouse.x !== null && mouse.y !== null) {
                    const dx = mouse.x - particle.x;
                    const dy = mouse.y - particle.y;
                    const dist = Math.hypot(dx, dy);
                    const magnetRadius = 200;
                    if (dist < magnetRadius) {
                        const strength = ((magnetRadius - dist) / magnetRadius) * 0.02;
                        particle.x += dx * strength;
                        particle.y += dy * strength;
                    }
                }
                particle.draw();
            });

            ctx.restore();

            animationId = requestAnimationFrame(animate);
        };

        createParticles();
        animate();

        const handleMouseMove = throttle((event) => {
            mouse.x = event.clientX;
            mouse.y = event.clientY;
            targetOffset.x = (event.clientX - width / 2) * 0.01;
            targetOffset.y = (event.clientY - height / 2) * 0.01;
        }, 16); // ~60fps

        const handleMouseOut = () => {
            mouse.x = null;
            mouse.y = null;
        }

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseout', handleMouseOut);

        // Auf Fenstergröße reagieren
        const handleResize = () => {
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = width;
            canvas.height = height;
        };
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseout', handleMouseOut);
            cancelAnimationFrame(animationId);
        };
    }, [enabled]);

    return (
        <canvas
            ref={canvasRef} className={"background-animation"}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                zIndex: 0,
                pointerEvents: 'none', // super wichtig: Canvas blockiert keine Klicks
            }}
        />
    );
}

export default React.memo(ParticlesCanvas);